import React, { useState, Component } from 'react';
import { ChevronDown, ChevronUp, Atom, Heart, Building2, ExternalLink, User, Server } from 'lucide-react';
// Custom Molecule icon - circles connected by lines
function MoleculeIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11">
      <circle cx="6" cy="12" r="3" data-id="element-12" />
      <circle cx="18" cy="6" r="3" data-id="element-13" />
      <circle cx="18" cy="18" r="3" data-id="element-14" />
      <line x1="8.5" y1="10.5" x2="15.5" y2="7.5" data-id="element-15" />
      <line x1="8.5" y1="13.5" x2="15.5" y2="16.5" data-id="element-16" />
    </svg>;
}
// Custom Cell icon - biological cell shape
function CellIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-17">
      <ellipse cx="12" cy="12" rx="9" ry="7" data-id="element-18" />
      <ellipse cx="12" cy="12" rx="3" ry="2" data-id="element-19" />
      <circle cx="7" cy="10" r="1" fill="currentColor" data-id="element-20" />
      <circle cx="16" cy="14" r="1" fill="currentColor" data-id="element-21" />
    </svg>;
}
// Diagram Components
function AtomDiagram() {
  return <div className="w-full bg-gradient-to-br from-cyan-50 to-white rounded-lg border border-cyan-100 flex items-center justify-center p-4" data-id="element-22">
      <img src="https://cdn.magicpatterns.com/uploads/qjAbVA8J62MtZq8EapASHz/atoms-sketch.png" alt="Atoms representing capabilities: Java (programming language), Security (technical discipline), and Public Speaking (practice)" className="max-h-48 w-auto object-contain" data-id="element-23" />
    </div>;
}
function MoleculeDiagram() {
  return <div className="w-full bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-100 flex items-center justify-center p-4" data-id="element-24">
      <img src="https://cdn.magicpatterns.com/uploads/v4Xr6xVHFpYWuA7U56iPTS/image.png" alt="Molecules: A Developer (person) with capabilities like JavaScript, Problem Solving, and Testing; and a Website (service) requiring capabilities like Security, Databases, and Analytics" className="max-h-56 w-auto object-contain" data-id="element-25" />
    </div>;
}
function CellDiagram() {
  return <div className="w-full bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100 flex items-center justify-center p-4" data-id="element-26">
      <img src="https://cdn.magicpatterns.com/uploads/6xKK9mic1oVtx6deHebdd6/cell-sketch.png" alt="A Cell (team) showing people working together with a shared purpose, surrounded by Capabilities, Services, Interactions, and Constraints" className="max-h-56 w-auto object-contain" data-id="element-27" />
    </div>;
}
function OrganDiagram() {
  return <div className="w-full bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100 flex items-center justify-center p-4" data-id="element-28">
      <img src="https://cdn.magicpatterns.com/uploads/gCK6k3JHfkVCGTR9M93X1L/image.png" alt="An Organ (department) represented as a heart containing multiple cells (teams), with connections to shared services, security, and coordination between teams" className="max-h-56 w-auto object-contain" data-id="element-29" />
    </div>;
}
function OrganismDiagram() {
  return <div className="h-40 w-full bg-gradient-to-br from-pink-50 to-white rounded-lg border border-pink-100 flex items-center justify-center p-4 relative overflow-hidden" data-id="element-30">
      {/* Outer organism boundary */}
      <div className="absolute inset-2 border-2 border-pink-200 rounded-full opacity-30" data-id="element-31"></div>
      <div className="flex flex-col items-center gap-2" data-id="element-32">
        {/* Simplified nested view */}
        <div className="flex items-center gap-3" data-id="element-33">
          <div className="w-12 h-12 border border-purple-200 rounded-lg bg-purple-50/50 flex items-center justify-center" data-id="element-34">
            <div className="w-6 h-6 border border-indigo-200 rounded-full bg-indigo-50/50 flex items-center justify-center" data-id="element-35">
              <div className="w-2 h-2 bg-blue-400 rounded-full" data-id="element-36"></div>
            </div>
          </div>
          <div className="w-12 h-12 border border-purple-200 rounded-lg bg-purple-50/50 flex items-center justify-center" data-id="element-37">
            <div className="w-6 h-6 border border-indigo-200 rounded-full bg-indigo-50/50 flex items-center justify-center" data-id="element-38">
              <div className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-39"></div>
            </div>
          </div>
        </div>
        <div className="text-[9px] text-slate-400 text-center" data-id="element-40">
          Living system that adapts & evolves
        </div>
      </div>
    </div>;
}
export function ConceptDeepDive() {
  const [openSection, setOpenSection] = useState<string | null>('Atom');
  const toggleSection = (name: string) => {
    setOpenSection(openSection === name ? null : name);
  };
  const concepts = [{
    name: 'Atom',
    icon: Atom,
    color: 'cyan',
    subtitle: 'Capability',
    githubUrl: 'https://github.com/atomicteamdesign/atd/blob/main/concepts/atoms.md',
    diagram: AtomDiagram,
    sections: [{
      title: 'What is an Atom?',
      content: "An atom is a single capability — a skill, competency, or area of expertise that contributes to meaningful work. Atoms are the smallest unit ATD concerns itself with. They're not roles, responsibilities, or tasks. They're the building blocks of what an organisation can actually do."
    }, {
      title: 'Examples',
      content: 'A programming language (Kotlin, Python, SQL). A technical discipline (system design, observability, security). A domain expertise (payments, compliance, onboarding). A practice (incident response, user research, technical writing).'
    }, {
      title: 'Why Capability, Not Role?',
      content: 'Roles bundle many capabilities together, mean different things in different companies, and become outdated faster than the skills they describe. Capabilities are composable, transferable, and evolve independently of org structure. Two people with the same title may share few atoms. Two people with different titles may share many.'
    }, {
      title: 'Atoms Are Contextual',
      content: '"Kubernetes" might mean deep platform expertise in an infrastructure team, operational awareness in a product team, or theoretical knowledge in a consulting role. ATD doesn\'t try to define a universal list of atoms. What matters is understanding the atoms your organisation needs, at the depth your context requires.'
    }]
  }, {
    name: 'Molecule',
    icon: MoleculeIcon,
    color: 'blue',
    subtitle: 'Composition',
    githubUrl: 'https://github.com/atomicteamdesign/atd/blob/main/concepts/molecules.md',
    diagram: MoleculeDiagram,
    sections: [{
      title: 'What is a Molecule?',
      content: 'A molecule is a composition of atoms bonded together to form something more complex. In ATD, molecules represent two things: People (individuals with their unique combination of capabilities) and Services (products, systems, or domains that require certain capabilities to operate).'
    }, {
      title: 'People as Molecules',
      content: "Every person is a unique molecule — a combination of skills, experience, depth, and potential that no one else exactly shares. A person's molecule includes the atoms they possess, the proficiency level of each, how those atoms relate and reinforce each other, and the potential for adjacent atoms to develop."
    }, {
      title: 'Services as Molecules',
      content: "Services also have a molecular structure — but instead of possessing atoms, they require them. A service molecule describes the capabilities needed to build it, run it, and change it safely, plus the criticality of each requirement. Not every requirement is equal. Some are critical — without them, the service can't function."
    }, {
      title: 'The Gap Is Your Risk',
      content: 'The relationship between people and services is where ATD becomes useful: people have capabilities, services require them. The gap between the two is your organisational risk. This shift makes structural problems visible earlier and reduces the need for reactive reorganisation.'
    }]
  }, {
    name: 'Cell',
    icon: CellIcon,
    color: 'indigo',
    subtitle: 'Team',
    githubUrl: 'https://github.com/atomicteamdesign/atd/blob/main/concepts/cells.md',
    diagram: CellDiagram,
    sections: [{
      title: 'What is a Cell?',
      content: "A cell is a team — a group of people working together toward a shared purpose. In ATD, cells are the primary unit of organisational structure. They're where capability meets work. A cell's purpose defines what it needs to do, which defines the capabilities it requires, which defines the people who should be part of it."
    }, {
      title: 'Purpose Defines the Cell',
      content: "Every cell should have a clear purpose — a statement of what it exists to do. Good purposes are specific enough to guide decisions about what's in and out of scope, stable enough to persist beyond any single project or quarter, and outcome-oriented rather than activity-oriented."
    }, {
      title: 'From Purpose to Requirements',
      content: 'Once you know the purpose, you can derive what the cell needs: Services owned (what products, systems, or domains does this cell operate?), Capabilities required (what atoms do those services need?), Interactions (what other cells does this one depend on or serve?), and Constraints (what regulations, SLAs, or limits shape how the cell works?).'
    }, {
      title: 'Composing the Cell',
      content: 'With requirements clear, you can assess whether the current team composition makes sense. Do we have coverage for all critical capabilities? Is any capability concentrated in a single person? Can we develop the capabilities we\'re missing? If a team has "the right people" but still can\'t deliver, the problem is usually structural, not individual.'
    }]
  }, {
    name: 'Organ',
    icon: Heart,
    color: 'purple',
    subtitle: 'Department',
    githubUrl: 'https://github.com/atomicteamdesign/atd/blob/main/concepts/organs.md',
    diagram: OrganDiagram,
    sections: [{
      title: 'What is an Organ?',
      content: "An organ is a collection of cells (teams) that together perform a critical function within the larger organism (organisation). If you're familiar with the Spotify model, think of organs as similar to tribes. In traditional org structures, they map roughly to departments or divisions. But where those terms often describe reporting lines, organs in ATD describe functional purpose."
    }, {
      title: 'When Organs Make Sense',
      content: "Not every organisation needs explicit organs. Smaller organisations — where a handful of cells constitute the whole organism — might skip this layer entirely. Organs become relevant when: scale exceeds single-cell capacity, related cells need coordination, or there's value in shared direction, standards, or capabilities across cells."
    }, {
      title: 'Organ Purpose',
      content: 'Like cells, organs should have a clear purpose. But organ purposes are broader — they describe a function that spans multiple teams. Examples: Platform (enable product teams to build and operate services without deep infrastructure expertise), Consumer Products (deliver valuable experiences to retail customers), Payments (process and reconcile all financial transactions reliably and compliantly).'
    }, {
      title: 'Capability Distribution',
      content: 'At the organ level, capability analysis shifts from "does this team have what it needs" to "does this collection of teams have what it needs." Some capabilities might be centralised in a single cell that serves the whole organ. Others might be distributed across cells, with coordination to ensure consistency.'
    }]
  }, {
    name: 'Organism',
    icon: Building2,
    color: 'pink',
    subtitle: 'Organisation',
    githubUrl: 'https://github.com/atomicteamdesign/atd/blob/main/concepts/organisms.md',
    diagram: OrganismDiagram,
    sections: [{
      title: 'What is an Organism?',
      content: "The organism is the organisation as a whole — the complete system of atoms, molecules, cells, and organs working together. An organism isn't just the sum of its parts. It's the product of how capability is distributed across the system, how teams and organs interact, how information flows and decisions get made, and how the whole thing adapts over time."
    }, {
      title: 'Living Systems, Not Machines',
      content: 'The biological metaphor in ATD is deliberate. Organisations behave more like living systems than machines. Machines are designed once, then operate as intended. They fail when components break and are repaired by replacing parts with identical parts. Living systems grow, adapt, and respond to their environment. They heal and compensate when parts are damaged. They change their own structure over time.'
    }, {
      title: 'How Organisms Evolve',
      content: "Healthy organisations change through recomposition, not reorganisation. Reorganisation means big-bang structural changes — everyone gets new boxes on the org chart. It's disruptive, expensive, often traumatic. Recomposition means continuous small adjustments to capability and structure. People move between cells as needs shift. Cells gain or shed services based on capacity and purpose."
    }, {
      title: 'Recomposition vs Reorganisation',
      content: 'Recomposition is maintenance. Reorganisation is surgery. You should need surgery rarely. You should be maintaining constantly. ATD provides the language to make recomposition deliberate. When you understand capability at the atom level, you can make small moves with confidence instead of waiting until problems are big enough to justify upheaval.'
    }]
  }];
  const colorClasses = {
    cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    pink: 'bg-pink-100 text-pink-700 border-pink-200'
  };
  return <section id="concept-deep-dive" className="py-16 md:py-24 bg-slate-50" data-id="element-41">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-42">
        <div className="text-center mb-12" data-id="element-43">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-44">
            Deep Dive into the Concepts
          </h2>
          <p className="text-lg text-slate-600" data-id="element-45">
            Each level builds on the one below. Understanding atoms is
            prerequisite to understanding molecules. Understanding molecules is
            prerequisite to designing cells. And so on.
          </p>
        </div>

        <div className="space-y-4" data-id="element-46">
          {concepts.map(concept => {
          const isOpen = openSection === concept.name;
          const DiagramComponent = concept.diagram;
          return <div key={concept.name} className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${isOpen ? 'shadow-lg border-primary/20' : 'shadow-sm border-slate-200'}`} data-id="element-47">
                <button onClick={() => toggleSection(concept.name)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-48">
                  <div className="flex items-center gap-4" data-id="element-49">
                    <div className={`p-2 rounded-lg ${colorClasses[concept.color as keyof typeof colorClasses]}`} data-id="element-50">
                      <concept.icon className="h-6 w-6" data-id="element-51" />
                    </div>
                    <div data-id="element-52">
                      <h3 className="text-xl font-bold text-slate-900" data-id="element-53">
                        {concept.name}
                      </h3>
                      <p className="text-sm text-slate-500 font-medium" data-id="element-54">
                        {concept.subtitle}
                      </p>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="h-5 w-5 text-slate-400" data-id="element-55" /> : <ChevronDown className="h-5 w-5 text-slate-400" data-id="element-56" />}
                </button>

                <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-57">
                  <div className="px-6 pb-6 border-t border-slate-100" data-id="element-58">
                    {/* Diagram */}
                    <div className="mt-6 mb-8" data-id="element-59">
                      <DiagramComponent data-id="element-60" />
                    </div>

                    {/* Content sections */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-61">
                      {concept.sections.map((section, idx) => <div key={idx} className={idx === 0 ? 'md:col-span-2' : ''} data-id="element-62">
                          <h4 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-63">
                            {section.title}
                          </h4>
                          <p className="text-slate-600 text-sm leading-relaxed" data-id="element-64">
                            {section.content}
                          </p>
                        </div>)}
                    </div>

                    {/* GitHub link */}
                    <div className="mt-6 pt-4 border-t border-slate-100" data-id="element-65">
                      <a href={concept.githubUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors" data-id="element-66">
                        Read full documentation on GitHub
                        <ExternalLink className="h-4 w-4" data-id="element-67" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}